/**
 * Esquema de IndexedDB con Dexie (design.md, sección 7.8).
 *
 * REGLAS DE VERSIONES (de Control de Negocio de Comida):
 * - Nunca se edita un bloque `.version(n)` existente. Cada cambio es
 *   `.version(n + 1)` con `.upgrade()` si hay que transformar datos, más su
 *   eslabón en `migrarDatos` (backup-format.ts) y un archivo de muestra.
 * - Crear una tabla nueva SÍ exige subir la versión.
 * - Un campo nuevo NO indexado no la exige: se declara opcional.
 * - No se indexan booleanos: IndexedDB no los acepta como clave.
 *
 * La versión 1 declara desde ya todas las tablas del diseño, aunque algunas
 * se usen hasta fases posteriores: así no hay migraciones solo para crearlas.
 */

import Dexie, { type Table } from 'dexie';
import { nuevoId } from './ids';
import { SETTINGS_PREDETERMINADOS } from './ajustes';
import {
  type Settings,
  type Scale,
  type Exercise,
  type Routine,
  type RoutineExercise,
  type Workout,
  type WorkoutExercise,
  type WorkoutSet,
  type WeighIn,
  type Measurement,
  type Food,
  type Dish,
  type MealEntry,
  type Day,
  type Plan,
  type Stage,
  type Maintenance,
  type Fast,
  type Photo,
  type PhotoBlob,
  TABLAS,
} from './types';
import { ahoraIso } from '@/domain/dates';

export const NOMBRE_BASE_DATOS = 'app-gym-salud';

/** Versión de Dexie más reciente declarada abajo. */
export const VERSION_DEXIE = 3;

/**
 * Las tablas e índices de la versión 1, tal cual. Se exportan para que las
 * pruebas creen una base de la versión 1 y comprueben la migración real.
 */
export const TABLAS_VERSION_1: Readonly<Record<string, string>> = Object.freeze({
  settings: 'id',
  scales: 'id',
  exercises: 'id, nombre, grupoMuscular',
  routines: 'id, orden',
  routineExercises: 'id, routineId, exerciseId',
  workouts: 'id, estado, fecha, inicio, routineId',
  workoutExercises: 'id, workoutId, exerciseId',
  workoutSets: 'id, workoutId, workoutExerciseId, exerciseId',
  weighIns: 'id, fecha, momento, scaleId',
  measurements: 'id, fecha, tipo, [tipo+fecha]',
  foods: 'id, nombre',
  dishes: 'id, nombre',
  mealEntries: 'id, fecha, momento',
  days: 'fecha',
  plans: 'id, estado, fechaInicio',
  stages: 'id, planId, fechaInicio',
  maintenance: 'id, desde',
  fasts: 'id, inicio',
});

/**
 * Esquema 3 (Fase 6, fotos de progreso): dos tablas nuevas. `photos` guarda
 * los datos de cada foto y entra al respaldo JSON; `photoBlobs` guarda los
 * bytes del JPEG y **no** entra al JSON, porque viajan como archivos del ZIP
 * (design.md 10.7). Van separadas para que sea imposible meter un Blob en el
 * JSON por descuido: ahí se volvería `{}` y la foto se perdería en silencio.
 */
export const TABLAS_VERSION_3: Readonly<Record<string, string>> = Object.freeze({
  ...TABLAS_VERSION_1,
  photos: 'id, fecha, momento',
  photoBlobs: 'id',
});

export class BaseDatos extends Dexie {
  settings!: Table<Settings, number>;
  scales!: Table<Scale, string>;
  exercises!: Table<Exercise, string>;
  routines!: Table<Routine, string>;
  routineExercises!: Table<RoutineExercise, string>;
  workouts!: Table<Workout, string>;
  workoutExercises!: Table<WorkoutExercise, string>;
  workoutSets!: Table<WorkoutSet, string>;
  weighIns!: Table<WeighIn, string>;
  measurements!: Table<Measurement, string>;
  foods!: Table<Food, string>;
  dishes!: Table<Dish, string>;
  mealEntries!: Table<MealEntry, string>;
  days!: Table<Day, string>;
  plans!: Table<Plan, string>;
  stages!: Table<Stage, string>;
  maintenance!: Table<Maintenance, string>;
  fasts!: Table<Fast, string>;
  photos!: Table<Photo, string>;
  photoBlobs!: Table<PhotoBlob, string>;

  constructor(nombre = NOMBRE_BASE_DATOS) {
    super(nombre);

    // Copias: Dexie conserva y amplía el objeto que recibe.
    this.version(1).stores({ ...TABLAS_VERSION_1 });

    // Versión 2 (Fase 1, Nutrición): las mismas tablas e índices. Los campos
    // nuevos (`estado` y `favoritosBase`) no se indexan, así que no cambia la
    // estructura; la versión sube para que, al abrir la app nueva, se guarde
    // la copia "antes de actualizar" (Req. 2.10) y para dejar los ajustes en
    // el esquema 2. `stores()` se declara igual a la versión 1 a propósito:
    // una versión sin `stores()` no hereda las tablas en Dexie.
    this.version(2)
      .stores({ ...TABLAS_VERSION_1 })
      .upgrade(async (tx) => {
        await tx
          .table('settings')
          .toCollection()
          .modify((ajustes: Record<string, unknown>) => {
            ajustes['schemaVersion'] = 2;
            if (!Array.isArray(ajustes['favoritosBase'])) ajustes['favoritosBase'] = [];
            // Las metas manuales que hubiera aplican desde siempre (null)
            if (!('metasManualesDesde' in ajustes)) ajustes['metasManualesDesde'] = null;
            if (!Array.isArray(ajustes['metasManualesAnteriores'])) ajustes['metasManualesAnteriores'] = [];
          });
      });

    // Versión 3 (Fase 6, fotos de progreso): dos tablas nuevas. Crear una
    // tabla sí exige subir la versión. No hay nada que transformar de lo que
    // ya estaba: solo se deja la marca del esquema en los ajustes.
    this.version(3)
      .stores({ ...TABLAS_VERSION_3 })
      .upgrade(async (tx) => {
        await tx
          .table('settings')
          .toCollection()
          .modify((ajustes: Record<string, unknown>) => {
            ajustes['schemaVersion'] = 3;
          });
      });
  }
}

export const db = new BaseDatos();

// ---------------------------------------------------------------------------
// Ajustes y perfil
// ---------------------------------------------------------------------------

// Los predeterminados viven en ajustes.ts, que es código puro y también usa
// la restauración; se reexportan aquí para no cambiar quién los importa.
export { SETTINGS_PREDETERMINADOS };

/** Ajustes nuevos, con su propio perfilId. */
export function settingsIniciales(): Settings {
  return {
    ...SETTINGS_PREDETERMINADOS,
    medidasOcultas: [],
    favoritosBase: [],
    metasManualesAnteriores: [],
    perfilId: nuevoId('per'),
    creadoEn: ahoraIso(),
  };
}

/**
 * LECTURA PURA de los ajustes. Nunca escribe.
 *
 * Es la única versión apta para `useLiveQuery`: Dexie ejecuta las consultas
 * reactivas en una transacción de solo lectura y prohíbe escribir en ellas
 * (lección de Control de Negocio de Comida en el iPhone).
 *
 * Si el registro todavía no existe, devuelve los predeterminados en memoria en
 * lugar de crearlo. Crearlo es tarea del arranque.
 */
export async function leerSettings(): Promise<Settings> {
  return (await db.settings.get(1)) ?? SETTINGS_PREDETERMINADOS;
}

/**
 * Crea el registro único de ajustes si no existe. ESCRIBE.
 *
 * Se llama al arrancar, nunca desde una consulta reactiva. Es idempotente:
 * repetirla no cambia el perfilId ya creado.
 */
export async function asegurarSettings(): Promise<Settings> {
  return db.transaction('rw', db.settings, async () => {
    const existente = await db.settings.get(1);
    if (existente !== undefined) {
      if (existente.perfilId !== '') return existente;
      // Defensa: un registro sin perfil (por ejemplo, restaurado de un archivo
      // incompleto) recibe uno nuevo en lugar de quedar vacío.
      const reparado = { ...existente, perfilId: nuevoId('per') };
      await db.settings.put(reparado);
      return reparado;
    }
    const nuevos = settingsIniciales();
    await db.settings.put(nuevos);
    return nuevos;
  });
}

/** ESCRIBE. Solo desde manejadores de eventos, nunca desde una consulta reactiva. */
export async function actualizarSettings(
  cambios: Partial<Omit<Settings, 'id' | 'perfilId' | 'schemaVersion' | 'creadoEn'>>,
): Promise<void> {
  await asegurarSettings();
  await db.settings.update(1, cambios);
}

// ---------------------------------------------------------------------------
// Diagnóstico
// ---------------------------------------------------------------------------

/** Cuenta los registros de todas las tablas. Para el resumen de respaldo. */
export async function contarRegistros(): Promise<Record<string, number>> {
  const conteos: Record<string, number> = {};
  // Las tablas que respalda el sistema de respaldos. `photoBlobs` queda fuera
  // a propósito: sus bytes son los de `photos` y contarlos dos veces engañaría
  await Promise.all(
    TABLAS.map(async (nombre) => {
      conteos[nombre] = await db.table(nombre).count();
    }),
  );
  return conteos;
}

/**
 * Lo capturado en este teléfono: todas las tablas salvo los ajustes y el
 * catálogo base de ejercicios, que la app crea sola. Es el mismo criterio que
 * `registrosCapturados` de los respaldos, para comparar teléfono y archivo.
 */
export async function totalRegistros(): Promise<number> {
  const conteos = await contarRegistros();
  const base = await db.exercises.filter((x) => x.esBase).count();
  return (
    Object.entries(conteos)
      .filter(([tabla]) => tabla !== 'settings')
      .reduce((suma, [, n]) => suma + n, 0) - base
  );
}

/**
 * ¿Hay datos del usuario que se perderían sin respaldo? (Req. 7.5)
 *
 * No cuentan los ajustes ni el catálogo base de ejercicios, que la app crea
 * sola: una instalación nueva no debe pedir respaldo.
 */
export async function hayDatosCapturados(): Promise<boolean> {
  const tablas = db.tables.filter((t) => t.name !== 'settings' && t.name !== 'exercises');
  for (const tabla of tablas) {
    if ((await tabla.count()) > 0) return true;
  }
  const propios = await db.exercises.filter((e) => !e.esBase).count();
  return propios > 0;
}
